import { CommentType, MutationType, ResourceType, STPageType, ShapeType } from '@tencent/slide-sdk-pb-model';
import { shallowOmit } from '../../utils/object-utils';
import type {
    IAddAnimationMutationData,
    IAddPageMutationData,
    IAddSectionMutationData,
    IAddShapeMutationData,
    IAddThemeMutationData,
    IDeleteTableColMutationData,
    IDeleteTableRowMutationData,
    IGroupMutationData,
    IInsertTableColMutationData,
    IInsertTableRowMutationData,
    IMergeTableCellsMutationData,
    IMoveAnimationMutationData,
    IMovePageMutationData,
    IBindSectionToSlideMutationData,
    IMoveShapeMutationData,
    IRemoveAnimationMutationData,
    IRemovePageMutationData,
    IRemoveSectionMutationData,
    IRemoveShapeMutationData,
    IRemoveThemeMutationData,
    ISetAnimationPropertiesMutationData,
    ISetPagePropertiesMutationData,
    ISetPresentationPropertiesMutationData,
    ISetSectionPropertiesMutationData,
    ISetShapePropertiesMutationData,
    ISetSlideSizeMutationData,
    ISetTableCellPropertiesMutationData,
    ISetTableColWidthsMutationData,
    ISetTableRowHeightsMutationData,
    ISetThemeMutationData,
    ITransformShapeMutationData,
    IUnmergeTableCellsMutationData,
    IUngroupMutationData,
    ISetChartSpacePropertiesMutationData,
    IAddSubChartMutationData,
    IMoveSubChartMutationData,
    IRemoveSubChartMutationData,
    ISetSubChartPropertiesMutationData,
    ISetTextDataMutationData,
    ISetTextBodyPropertiesMutationData,
    ITableRangeData,
    ISetNotesSizeMutationData,
    IUpdateResourceMapMutationData,
    IComputeGroupTransformMutationData,
    ISetDiagramDataMutationData,
    ISetDiagramDrawingMutationData,
    ISetDiagramsMutationData,
    IAddCommentMutationData,
    IRemoveCommentMutationData,
    ISetCommentPropertiesMutationData,
    ISetDataExtensionMutationData,
    IRemoveDataExtensionMutationData,
} from '@tencent/slide-sdk-pb-model';
import type { MarkRequired } from 'ts-essentials';

// 更新幻灯片属性
export type IValidSetPresentationPropertiesMutationData = MarkRequired<
    Omit<ISetPresentationPropertiesMutationData, 'version'>,
    'type'
>;
// 更新页面大小
export type IValidSetSlideSizeMutationData = Required<Omit<ISetSlideSizeMutationData, 'version'>>;
// 添加页面
export type IValidAddPageMutationData = MarkRequired<
    Omit<IAddPageMutationData, 'version'>,
    'type' | 'pageId' | 'shapeTreeId' | 'index'
> & {
    pageType: Exclude<STPageType, STPageType.PBDEFAULT>;
};
// 删除页面
export type IValidRemovePageMutationData = Required<Omit<IRemovePageMutationData, 'version'>> & {
    pageType: Exclude<STPageType, STPageType.PBDEFAULT>;
};
// 移动页面
export type IValidMovePageMutationData = Required<Omit<IMovePageMutationData, 'version'>>;
// 更新页属性
export type IValidSetPagePropertiesMutationData = MarkRequired<
    Omit<ISetPagePropertiesMutationData, 'version'>,
    'type' | 'pageId' | 'pageType'
>;
// 添加形状
export type IValidAddShapeMutationData = MarkRequired<
    Omit<IAddShapeMutationData, 'version' | 'shapeData'>,
    'type' | 'pageId' | 'shapeId' | 'shapeType' | 'parentId' | 'index'
> & {
    shapeType: Exclude<ShapeType, ShapeType.Unknown>;
};
// 删除形状
export type IValidRemoveShapeMutationData = Required<Omit<IRemoveShapeMutationData, 'version'>>;
// 更新形状的位置、大小、翻转
export type IValidTransformShapeMutationData = MarkRequired<
    Omit<ITransformShapeMutationData, 'version'>,
    'type' | 'pageId' | 'shapeId' | 'shapeType'
>;
export type IValidComputeGroupTransformMutationData = Required<
    Omit<IComputeGroupTransformMutationData, 'version' | 'shapeIds'>
> & {
    shapeIds?: string[];
};
// 不支持的 Mutation，仅做 Keep
export type IValidEmptyMutationData = { type: MutationType; payload: unknown };
// 更新形状层级
export type IValidMoveShapeMutationData = Required<Omit<IMoveShapeMutationData, 'version'>>;
// 组合形状
export type IValidGroupMutationData = Required<Omit<IGroupMutationData, 'version'>>;
// 解除形状组合
export type IValidUngroupMutationData = Required<Omit<IUngroupMutationData, 'version'>>;
// 更新形状属性
export type IValidSetShapePropertiesMutationData = Required<Omit<ISetShapePropertiesMutationData, 'version'>>;
// 添加页面章节
export type IValidAddSectionMutationData = Required<Omit<IAddSectionMutationData, 'version'>>;
// 删除页面章节
export type IValidRemoveSectionMutationData = Required<Omit<IRemoveSectionMutationData, 'version'>>;
// 更新页面章节
export type IValidSetSectionPropertiesMutationData = Required<Omit<ISetSectionPropertiesMutationData, 'version'>>;
// 移动页面章节
export type IValidBindSectionToSlideMutationData = Required<Omit<IBindSectionToSlideMutationData, 'version'>>;
// 插入表格行
export type IValidInsertTableRowMutationData = Required<Omit<IInsertTableRowMutationData, 'version'>>;
// 插入表格列
export type IValidInsertTableColMutationData = Required<Omit<IInsertTableColMutationData, 'version'>>;
// 删除表格行
export type IValidDeleteTableRowMutationData = Required<Omit<IDeleteTableRowMutationData, 'version'>>;
// 删除表格列
export type IValidDeleteTableColMutationData = Required<Omit<IDeleteTableColMutationData, 'version'>>;
// 合并表格单元格
export type IValidMergeTableCellsMutationData = Required<Omit<IMergeTableCellsMutationData, 'version' | 'range'>> & {
    range: Required<ITableRangeData>;
};
// 解除表格合并单元格
export type IValidUnmergeTableCellsMutationData = Required<
    Omit<IUnmergeTableCellsMutationData, 'version' | 'range'>
> & {
    range: Required<ITableRangeData>;
};

// 更新表格列宽度
export type IValidSetTableColWidthsMutationData = Required<Omit<ISetTableColWidthsMutationData, 'version'>>;
// 更新表格行高度
export type IValidSetTableRowHeightsMutationData = Required<Omit<ISetTableRowHeightsMutationData, 'version'>>;
// 更新表格单元格属性
export type IValidSetTableCellPropertiesMutationData = Required<
    Omit<ISetTableCellPropertiesMutationData, 'version' | 'range'>
> & { range: Required<ITableRangeData> };
// 添加动画
export type IValidAddAnimationMutationData = Required<Omit<IAddAnimationMutationData, 'version'>>;
// 删除动画
export type IValidRemoveAnimationMutationData = Required<Omit<IRemoveAnimationMutationData, 'version'>>;
// 更新动画
export type IValidSetAnimationPropertiesMutationData = Required<Omit<ISetAnimationPropertiesMutationData, 'version'>>;
// 调整动画顺序
export type IValidMoveAnimationMutationData = Required<Omit<IMoveAnimationMutationData, 'version'>>;
// 添加主题
export type IValidAddThemeMutationData = Required<Omit<IAddThemeMutationData, 'version'>>;
// 删除主题
export type IValidRemoveThemeMutationData = Required<Omit<IRemoveThemeMutationData, 'version'>>;
// 更新主题
export type IValidSetThemeMutationData = Required<Omit<ISetThemeMutationData, 'version'>>;
// 设置文本数据
export type IValidSetTextDataMutationData = Required<Omit<ISetTextDataMutationData, 'version'>>;
// 设置文本框属性
export type IValidSetTextBodyPropertiesMutationData = MarkRequired<
    Omit<ISetTextBodyPropertiesMutationData, 'version'>,
    'type' | 'pageId' | 'shapeId'
>;
// 更新图表外层ChartSpace
export type IValidSetChartSpacePropertiesMutationData = Required<Omit<ISetChartSpacePropertiesMutationData, 'version'>>;
// 添加子表
export type IValidAddSubChartMutationData = Required<Omit<IAddSubChartMutationData, 'version'>>;
// 删除子表
export type IValidRemoveSubChartMutationData = Required<Omit<IRemoveSubChartMutationData, 'version'>>;
// 移动子表
export type IValidMoveSubChartMutationData = Required<Omit<IMoveSubChartMutationData, 'version'>>;
// 设置子表属性
export type IValidSetSubChartPropertiesMutationData = Required<Omit<ISetSubChartPropertiesMutationData, 'version'>>;
// 设置备注大小
export type IValidSetNotesSizeMutationData = Required<Omit<ISetNotesSizeMutationData, 'version'>>;
// SmartArt - 设置 Data 数据
export type IValidSetDiagramDataMutationData = Required<Omit<ISetDiagramDataMutationData, 'version'>>;
// SmartArt - 设置 Drawing 数据
export type IValidSetDiagramDrawingMutationData = Required<Omit<ISetDiagramDrawingMutationData, 'version'>>;
// SmartArt - 设置 Diagram 数据
export type IValidSetDiagramsMutationData = Required<Omit<ISetDiagramsMutationData, 'version'>>;
// 添加批注
export type IValidAddCommentMutationData = MarkRequired<
    Omit<IAddCommentMutationData, 'version'>,
    'type' | 'pageId' | 'commentType' | 'groupId'
>;
// 删除批注
export type IValidRemoveCommentMutationData = MarkRequired<
    Omit<IRemoveCommentMutationData, 'version'>,
    'type' | 'pageId' | 'commentType' | 'groupId'
>;
// 更新批注
export type IValidSetCommentPropertiesMutationData = MarkRequired<
    Omit<ISetCommentPropertiesMutationData, 'version'>,
    'type' | 'pageId' | 'commentType' | 'groupId'
>;
// 更新资源映射表
export type IValidUpdateResourceMapMutationData = MarkRequired<
    Omit<IUpdateResourceMapMutationData, 'version'>,
    'type' | 'id' | 'resourceType'
>;
// 设置自定义扩展数据
export type IValidSetDataExtensionMutationData = MarkRequired<
    Omit<ISetDataExtensionMutationData, 'version'>,
    'type' | 'key' | 'value'
>;
// 删除自定义扩展数据
export type IValidRemoveDataExtensionMutationData = MarkRequired<
    Omit<IRemoveDataExtensionMutationData, 'version'>,
    'type'
>;

export type IValidMutationData =
    | IValidSetPresentationPropertiesMutationData
    | IValidSetSlideSizeMutationData
    | IValidAddPageMutationData
    | IValidRemovePageMutationData
    | IValidMovePageMutationData
    | IValidSetPagePropertiesMutationData
    | IValidAddShapeMutationData
    | IValidRemoveShapeMutationData
    | IValidTransformShapeMutationData
    | IValidMoveShapeMutationData
    | IValidGroupMutationData
    | IValidUngroupMutationData
    | IValidSetShapePropertiesMutationData
    | IValidAddSectionMutationData
    | IValidRemoveSectionMutationData
    | IValidSetSectionPropertiesMutationData
    | IValidBindSectionToSlideMutationData
    | IValidInsertTableRowMutationData
    | IValidInsertTableColMutationData
    | IValidDeleteTableRowMutationData
    | IValidDeleteTableColMutationData
    | IValidMergeTableCellsMutationData
    | IValidUnmergeTableCellsMutationData
    | IValidSetTableColWidthsMutationData
    | IValidSetTableRowHeightsMutationData
    | IValidSetTableCellPropertiesMutationData
    | IValidAddAnimationMutationData
    | IValidRemoveAnimationMutationData
    | IValidSetAnimationPropertiesMutationData
    | IValidMoveAnimationMutationData
    | IValidAddThemeMutationData
    | IValidRemoveThemeMutationData
    | IValidSetTextDataMutationData
    | IValidSetChartSpacePropertiesMutationData
    | IValidAddSubChartMutationData
    | IValidMoveSubChartMutationData
    | IValidRemoveSubChartMutationData
    | IValidSetSubChartPropertiesMutationData
    | IValidSetNotesSizeMutationData
    | IValidUpdateResourceMapMutationData
    | IValidComputeGroupTransformMutationData
    | IValidSetDiagramDataMutationData
    | IValidSetDiagramDrawingMutationData
    | IValidSetDiagramsMutationData
    | IValidAddCommentMutationData
    | IValidRemoveCommentMutationData
    | IValidSetCommentPropertiesMutationData
    | IValidSetDataExtensionMutationData
    | IValidRemoveDataExtensionMutationData;

export type MaybeMutationData = { type: MutationType };

const validators: Record<
    Exclude<MutationType, MutationType.Empty>,
    (data: MaybeMutationData) => IValidMutationData | undefined
> = {
    [MutationType.SetPresentationProperties]: ValidSetPresentationPropertiesMutationData,
    [MutationType.SetSlideSize]: ValidSetSlideSizeMutationData,
    [MutationType.AddPage]: ValidAddPageMutationData,
    [MutationType.RemovePage]: ValidRemovePageMutationData,
    [MutationType.MovePage]: ValidMovePageMutationData,
    [MutationType.SetPageProperties]: ValidSetPagePropertiesMutationData,
    [MutationType.AddShape]: ValidAddShapeMutationData,
    [MutationType.RemoveShape]: ValidRemoveShapeMutationData,
    [MutationType.TransformShape]: ValidTransformShapeMutationData,
    [MutationType.MoveShape]: ValidMoveShapeMutationData,
    [MutationType.Group]: ValidGroupMutationData,
    [MutationType.Ungroup]: ValidUngroupMutationData,
    [MutationType.SetShapeProperties]: ValidSetShapePropertiesMutationData,
    [MutationType.AddSection]: ValidAddSectionMutationData,
    [MutationType.RemoveSection]: ValidRemoveSectionMutationData,
    [MutationType.SetSectionProperties]: ValidSetSectionPropertiesMutationData,
    [MutationType.BindSectionToSlide]: ValidBindSectionToSlideMutationData,
    [MutationType.InsertTableRow]: ValidInsertTableRowMutationData,
    [MutationType.InsertTableCol]: ValidInsertTableColMutationData,
    [MutationType.DeleteTableRow]: ValidDeleteTableRowMutationData,
    [MutationType.DeleteTableCol]: ValidDeleteTableColMutationData,
    [MutationType.MergeTableCells]: ValidMergeTableCellsMutationData,
    [MutationType.UnmergeTableCells]: ValidUnMergeTableCellsMutationData,
    [MutationType.SetTableColWidths]: ValidSetTableColWidthsMutationData,
    [MutationType.SetTableRowHeights]: ValidSetTableRowHeightsMutationData,
    [MutationType.SetTableCellProperties]: ValidSetTableCellPropertiesMutationData,
    [MutationType.AddAnimation]: ValidAddAnimationMutationData,
    [MutationType.RemoveAnimation]: ValidRemoveAnimationMutationData,
    [MutationType.SetAnimationProperties]: ValidSetAnimationPropertiesMutationData,
    [MutationType.MoveAnimation]: ValidMoveAnimationMutationData,
    [MutationType.AddTheme]: ValidAddThemeMutationData,
    [MutationType.RemoveTheme]: ValidRemoveThemeMutationData,
    [MutationType.SetTheme]: ValidSetThemeMutationData,
    [MutationType.SetTextData]: ValidSetTextDataMutationData,
    [MutationType.SetTextBodyProperties]: ValidSetTextBodyPropertiesMutationData,
    [MutationType.SetChartSpaceProperties]: ValidSetChartSpacePropertiesMutationData,
    [MutationType.AddSubChart]: ValidAddSubChartMutationData,
    [MutationType.SetSubChartProperties]: ValidSetSubChartPropertiesMutationData,
    [MutationType.RemoveSubChart]: ValidRemoveSubChartMutationData,
    [MutationType.ComputeGroupTransform]: ValidComputeGroupTransformMutationData,
    [MutationType.SetDiagramData]: ValidSetDiagramDataMutationData,
    [MutationType.SetDiagramDrawing]: ValidSetDiagramDrawingMutationData,
    [MutationType.SetDiagrams]: ValidSetDiagramsMutationData,
    [MutationType.MoveSubChart]: ValidMoveSubChartMutationData,
    // TODO 实现下面图表的其他Valid
    [MutationType.UpdateResourceMap]: ValidUpdateResourceMapMutationData,

    // TODO 待补充
    [MutationType.InsertText]: (data) => data,
    [MutationType.DeleteText]: (data) => data,
    [MutationType.SetTextProperties]: (data) => data,
    [MutationType.GroupAsSmartArt]: (data) => data,
    [MutationType.SetNotesSize]: (data) => data,

    [MutationType.AddComment]: ValidAddCommentMutationData,
    [MutationType.RemoveComment]: ValidRemoveCommentMutationData,
    [MutationType.SetCommentProperties]: ValidSetCommentPropertiesMutationData,

    [MutationType.SetDataExtension]: ValidSetDataExtensionData,
    [MutationType.RemoveDataExtension]: ValidRemoveDataExtensionData,
};

export function ValidMutationData(data: MaybeMutationData): IValidMutationData | undefined {
    if (!data.type) return;

    return validators[data.type]?.(data);
}

export function ValidSetChartSpacePropertiesMutationData({
    shapeId,
    pageId,
    chartSpacePr,
}: ISetChartSpacePropertiesMutationData): IValidSetChartSpacePropertiesMutationData | undefined {
    if (chartSpacePr && shapeId && pageId) {
        return {
            type: MutationType.SetChartSpaceProperties,
            shapeId,
            pageId,
            chartSpacePr,
        };
    }
}

export function ValidSetSubChartPropertiesMutationData({
    shapeId,
    pageId,
    index,
    plot,
    axesPr,
}: ISetSubChartPropertiesMutationData): IValidSetSubChartPropertiesMutationData | undefined {
    if (shapeId && pageId) {
        return {
            type: MutationType.SetSubChartProperties,
            shapeId,
            pageId,
            index: index ?? 0,
            plot: plot || {},
            axesPr: axesPr ?? null,
        };
    }
}

export function ValidAddSubChartMutationData({
    shapeId,
    pageId,
    index,
    subChartType,
}: IAddSubChartMutationData): IValidAddSubChartMutationData | undefined {
    if (shapeId && pageId && subChartType) {
        return {
            type: MutationType.AddSubChart,
            shapeId,
            pageId,
            index: index ?? 0,
            subChartType,
        };
    }
}

export function ValidRemoveSubChartMutationData({
    shapeId,
    pageId,
    index,
}: IAddSubChartMutationData): IValidRemoveSubChartMutationData | undefined {
    if (shapeId && pageId) {
        return {
            type: MutationType.RemoveSubChart,
            shapeId,
            pageId,
            index: index ?? 0,
        };
    }
}

export function ValidMoveSubChartMutationData({
    shapeId,
    pageId,
    destIndex,
    srcIndex,
}: IMoveSubChartMutationData): IValidMoveSubChartMutationData | undefined {
    if (shapeId && pageId) {
        return {
            type: MutationType.MoveSubChart,
            shapeId,
            pageId,
            srcIndex: srcIndex ?? 0,
            destIndex: destIndex ?? 0,
        };
    }
}

export function ValidAddThemeMutationData({
    themeId,
    themeName,
    themeElements,
}: IAddThemeMutationData): IValidAddThemeMutationData | undefined {
    if (themeId && themeElements) {
        return { type: MutationType.AddTheme, themeId, themeName: themeName ?? '', themeElements };
    }
}

export function ValidRemoveThemeMutationData({
    themeId,
}: IRemoveThemeMutationData): IValidRemoveThemeMutationData | undefined {
    if (themeId) {
        return {
            type: MutationType.RemoveTheme,
            themeId,
        };
    }
}

export function ValidSetThemeMutationData({
    themeId,
    themeName,
    themeElements,
}: ISetThemeMutationData): IValidSetThemeMutationData | undefined {
    if (themeId && themeElements) {
        return {
            type: MutationType.SetTheme,
            themeId,
            themeName: themeName ?? '',
            themeElements,
        };
    }
}

export function ValidMoveAnimationMutationData({
    pageId,
    shapeId,
    fromTriggerId,
    triggerId,
    fromSeqType,
    seqType,
    fromIndex,
    index,
}: IMoveAnimationMutationData): IValidMoveAnimationMutationData | undefined {
    if (seqType && fromSeqType && triggerId && fromTriggerId && shapeId && pageId) {
        return {
            type: MutationType.MoveAnimation,
            index: index ?? 0,
            fromIndex: fromIndex ?? 0,
            seqType,
            fromSeqType,
            triggerId,
            fromTriggerId,
            shapeId,
            pageId,
        };
    }
}

export function ValidSetAnimationPropertiesMutationData({
    pageId,
    shapeId,
    triggerId,
    seqType,
    index,
    timeNode,
}: ISetAnimationPropertiesMutationData): IValidSetAnimationPropertiesMutationData | undefined {
    if (pageId && shapeId && triggerId && seqType && timeNode) {
        return {
            type: MutationType.SetAnimationProperties,
            pageId,
            shapeId,
            triggerId,
            seqType,
            index: index ?? 0,
            timeNode,
        };
    }
}

export function ValidRemoveAnimationMutationData({
    pageId,
    shapeId,
    triggerId,
    seqType,
    index,
}: IRemoveAnimationMutationData): IValidRemoveAnimationMutationData | undefined {
    if (seqType && triggerId && shapeId && pageId) {
        return {
            type: MutationType.RemoveAnimation,
            index: index ?? 0,
            seqType,
            triggerId,
            shapeId,
            pageId,
        };
    }
}

export function ValidAddAnimationMutationData({
    pageId,
    shapeId,
    triggerId,
    seqType,
    index,
}: IAddAnimationMutationData): IValidAddAnimationMutationData | undefined {
    if (pageId && shapeId && triggerId && seqType) {
        return {
            type: MutationType.AddAnimation,
            pageId,
            shapeId,
            triggerId,
            seqType,
            index: index ?? 0,
        };
    }
}

export function ValidSetTableCellPropertiesMutationData({
    pageId,
    shapeId,
    range,
    tableCellProperties,
}: ISetTableCellPropertiesMutationData): IValidSetTableCellPropertiesMutationData | undefined {
    const validRange = ValidTableRangeData(range);
    if (pageId && shapeId && validRange && tableCellProperties !== undefined)
        return {
            type: MutationType.SetTableCellProperties,
            pageId,
            shapeId,
            range: validRange,
            tableCellProperties,
        };
}

export function ValidSetTableRowHeightsMutationData({
    pageId,
    shapeId,
    heights,
}: ISetTableRowHeightsMutationData): IValidSetTableRowHeightsMutationData | undefined {
    if (pageId && shapeId && heights) {
        return {
            type: MutationType.SetTableRowHeights,
            pageId,
            shapeId,
            heights: heights.map(({ index, height }) => ({
                index: index ?? 0,
                height: height ?? 0,
            })),
        };
    }
}

export function ValidSetTableColWidthsMutationData({
    pageId,
    shapeId,
    widths,
}: ISetTableColWidthsMutationData): IValidSetTableColWidthsMutationData | undefined {
    if (pageId && shapeId && widths) {
        return {
            type: MutationType.SetTableColWidths,
            pageId,
            shapeId,
            widths: widths.map(({ index, width }) => ({
                index: index ?? 0,
                width: width ?? 0,
            })),
        };
    }
}

export function ValidUnMergeTableCellsMutationData({
    pageId,
    shapeId,
    range,
}: IUnmergeTableCellsMutationData): IValidUnmergeTableCellsMutationData | undefined {
    const validRange = ValidTableRangeData(range);
    if (pageId && shapeId && validRange) {
        return {
            type: MutationType.UnmergeTableCells,
            pageId,
            shapeId,
            range: validRange,
        };
    }
}

export function ValidMergeTableCellsMutationData({
    pageId,
    shapeId,
    range,
}: IMergeTableCellsMutationData): IValidMergeTableCellsMutationData | undefined {
    const validRange = ValidTableRangeData(range);
    if (pageId && shapeId && validRange) {
        return {
            type: MutationType.MergeTableCells,
            pageId,
            shapeId,
            range: validRange,
        };
    }
}

export function ValidDeleteTableColMutationData({
    pageId,
    shapeId,
    index,
    count,
}: IDeleteTableColMutationData): IValidDeleteTableColMutationData | undefined {
    if (pageId && shapeId) {
        return {
            type: MutationType.DeleteTableCol,
            pageId,
            shapeId,
            index: index ?? 0,
            count: count ?? 0,
        };
    }
}

export function ValidDeleteTableRowMutationData({
    pageId,
    shapeId,
    index,
    count,
}: IDeleteTableRowMutationData): IValidDeleteTableRowMutationData | undefined {
    if (pageId && shapeId) {
        return {
            type: MutationType.DeleteTableRow,
            pageId,
            shapeId,
            index: index ?? 0,
            count: count ?? 0,
        };
    }
}

export function ValidInsertTableColMutationData({
    pageId,
    shapeId,
    index,
    count,
}: IInsertTableColMutationData): IValidInsertTableColMutationData | undefined {
    if (pageId && shapeId) {
        return {
            type: MutationType.InsertTableCol,
            pageId,
            shapeId,
            index: index ?? 0,
            count: count ?? 0,
        };
    }
}

export function ValidInsertTableRowMutationData({
    pageId,
    shapeId,
    index,
    count,
}: IInsertTableRowMutationData): IValidInsertTableRowMutationData | undefined {
    if (pageId && shapeId) {
        return {
            type: MutationType.InsertTableRow,
            pageId,
            shapeId,
            index: index ?? 0,
            count: count ?? 0,
        };
    }
}

export function ValidBindSectionToSlideMutationData({
    sectionId,
    firstSlideIndex,
}: IBindSectionToSlideMutationData): IValidBindSectionToSlideMutationData | undefined {
    if (sectionId) {
        return {
            type: MutationType.BindSectionToSlide,
            sectionId,
            firstSlideIndex: firstSlideIndex ?? 0,
        };
    }
}

export function ValidSetSectionPropertiesMutationData({
    sectionId,
    name,
}: ISetSectionPropertiesMutationData): IValidSetSectionPropertiesMutationData | undefined {
    if (sectionId) {
        return {
            type: MutationType.SetSectionProperties,
            sectionId,
            name: name ?? '',
        };
    }
}

export function ValidRemoveSectionMutationData({
    sectionId,
}: IRemoveSectionMutationData): IValidRemoveSectionMutationData | undefined {
    if (sectionId) {
        return {
            type: MutationType.RemoveSection,
            sectionId,
        };
    }
}

export function ValidAddSectionMutationData({
    sectionId,
    name,
}: IAddSectionMutationData): IValidAddSectionMutationData | undefined {
    if (sectionId) {
        return {
            type: MutationType.AddSection,
            sectionId,
            name: name ?? '',
        };
    }
}

export function ValidSetShapePropertiesMutationData({
    pageId,
    shapeId,
    shapeDelta,
}: ISetShapePropertiesMutationData): IValidSetShapePropertiesMutationData | undefined {
    if (pageId && shapeId && shapeDelta) {
        return {
            type: MutationType.SetShapeProperties,
            pageId,
            shapeId,
            shapeDelta: {
                ...shapeDelta,
                spPr: shapeDelta.spPr && shallowOmit(shapeDelta.spPr, 'xfrm'),
            },
        };
    }
}

export function ValidUngroupMutationData({
    pageId,
    groupShapeId,
    shapeIds,
    parentId,
    index,
}: IUngroupMutationData): IValidUngroupMutationData | undefined {
    if (pageId && groupShapeId && parentId) {
        return {
            type: MutationType.Ungroup,
            pageId,
            groupShapeId,
            // Ungroup 允许内部不存在子元素的情况
            shapeIds: shapeIds ?? [],
            parentId,
            index: index ?? 0,
        };
    }
}

export function ValidGroupMutationData({
    pageId,
    groupShapeId,
    shapeIds,
    isSmartArt,
    parentId,
    index,
}: IGroupMutationData): IValidGroupMutationData | undefined {
    if (pageId && groupShapeId) {
        return {
            type: MutationType.Group,
            pageId,
            groupShapeId,
            shapeIds: shapeIds ?? [],
            isSmartArt: !!isSmartArt,
            parentId: parentId ?? '',
            index: index ?? 0,
        };
    }
}

export function ValidMoveShapeMutationData({
    pageId,
    shapeId,
    opType,
    destShapeId,
    srcParentId,
    destParentId,
    srcIndex,
    destIndex,
}: IMoveShapeMutationData): IValidMoveShapeMutationData | undefined {
    if (pageId && shapeId && srcParentId && destParentId) {
        return {
            type: MutationType.MoveShape,
            pageId,
            shapeId,
            destShapeId: destShapeId ?? '',
            opType: opType ?? 0,
            srcParentId,
            destParentId,
            srcIndex: srcIndex ?? 0,
            destIndex: destIndex ?? 0,
        };
    }
}

export function ValidTransformShapeMutationData({
    pageId,
    shapeId,
    parentId,
    shapeType,
    xfrmDelta,
    chXfrmDelta,
    clearXfrm,
}: ITransformShapeMutationData): IValidTransformShapeMutationData | undefined {
    // 是否有 xfrm 变更
    const hasXfrmDelta = !!(xfrmDelta || chXfrmDelta);
    // 必居其一，且互斥
    if (clearXfrm === hasXfrmDelta) {
        return;
    }

    // 只有 GroupShape 可以变更 xfrm.ch_xxx
    if (shapeType !== ShapeType.GroupShape && chXfrmDelta) {
        return;
    }

    if (pageId && shapeId && shapeType) {
        return {
            type: MutationType.TransformShape,
            pageId,
            shapeId,
            parentId: parentId ?? '',
            shapeType,
            xfrmDelta,
            chXfrmDelta,
            clearXfrm,
        };
    }
}

export function ValidRemoveShapeMutationData({
    pageId,
    shapeId,
    parentId,
    index,
}: IRemoveShapeMutationData): IValidRemoveShapeMutationData | undefined {
    if (pageId && shapeId && parentId) {
        return {
            type: MutationType.RemoveShape,
            pageId,
            shapeId,
            parentId,
            index: index ?? 0,
        };
    }
}

export function ValidAddShapeMutationData({
    pageId,
    shapeId,
    shapeType,
    tableOptions,
    parentId,
    index,
}: IAddShapeMutationData): IValidAddShapeMutationData | undefined {
    if (pageId && shapeId && shapeType) {
        return {
            type: MutationType.AddShape,
            pageId,
            shapeId,
            shapeType,
            tableOptions,
            parentId: parentId ?? '',
            index: index ?? 0,
        };
    }
}

// eslint-disable-next-line complexity
export function ValidSetPagePropertiesMutationData({
    pageId,
    pageType,
    pageProperties,
    slideProperties,
    layoutProperties,
    masterProperties,
    notesProperties,
    notesMasterProperties,
}: ISetPagePropertiesMutationData): IValidSetPagePropertiesMutationData | undefined {
    if (pageType === STPageType.slide && !slideProperties && !pageProperties) {
        return;
    }

    if (pageType === STPageType.slideLayout && !layoutProperties && !pageProperties) {
        return;
    }

    if (pageType === STPageType.slideMaster && !masterProperties && !pageProperties) {
        return;
    }

    if (pageType === STPageType.notes && !notesProperties && !pageProperties) {
        return;
    }

    if (pageType === STPageType.notesMaster && !notesMasterProperties && !pageProperties) {
        return;
    }

    if (pageId && pageType) {
        return {
            type: MutationType.SetPageProperties,
            pageId,
            pageType,
            pageProperties,
            slideProperties,
            layoutProperties,
            masterProperties,
            notesProperties,
            notesMasterProperties,
        };
    }
}

export function ValidMovePageMutationData({
    pageId,
    pageType,
    srcParentPageId,
    srcIndex,
    destParentPageId,
    destIndex,
}: IMovePageMutationData): IValidMovePageMutationData | undefined {
    if (pageId && pageType) {
        return {
            type: MutationType.MovePage,
            pageId,
            pageType,
            srcParentPageId: srcParentPageId ?? '',
            srcIndex: srcIndex ?? 0,
            destParentPageId: destParentPageId ?? '',
            destIndex: destIndex ?? 0,
        };
    }
}

export function ValidRemovePageMutationData({
    pageId,
    pageType,
    parentId,
    index,
}: IRemovePageMutationData): IValidRemovePageMutationData | undefined {
    if (pageId && pageType) {
        return {
            type: MutationType.RemovePage,
            pageId,
            pageType,
            parentId: parentId ?? '',
            index: index ?? 0,
        };
    }
}

export function ValidAddPageMutationData({
    pageId,
    shapeTreeId,
    index,
    pageType,
    slideOptions,
    slideMasterOptions,
    slideLayoutOptions,
    notesOptions,
    notesMasterOptions,
}: IAddPageMutationData): IValidAddPageMutationData | undefined {
    if (pageId && pageType && shapeTreeId) {
        return {
            type: MutationType.AddPage,
            pageType,
            index: index ?? 0,
            shapeTreeId,
            pageId,
            slideOptions,
            slideMasterOptions,
            slideLayoutOptions,
            notesOptions,
            notesMasterOptions,
        };
    }
}

export function ValidSetSlideSizeMutationData({
    size,
}: ISetSlideSizeMutationData): IValidSetSlideSizeMutationData | undefined {
    if (size) {
        return { type: MutationType.SetSlideSize, size };
    }
}

export function ValidSetPresentationPropertiesMutationData({
    presentationPr,
    defaultTextStyle,
    tableStyleList,
    embeddedFontList,
    viewPr,
    showSpecialPlsOnTitleSld,
    firstSlideNum,
    slideGuideList,
    modifyVerifier,
}: ISetPresentationPropertiesMutationData): IValidSetPresentationPropertiesMutationData | undefined {
    return {
        type: MutationType.SetPresentationProperties,
        presentationPr,
        defaultTextStyle,
        tableStyleList,
        embeddedFontList,
        viewPr,
        showSpecialPlsOnTitleSld,
        firstSlideNum,
        slideGuideList,
        modifyVerifier,
    };
}

export function ValidSetTextDataMutationData({
    pageId,
    shapeId,
    text,
    row,
    col,
    textProperties,
}: ISetTextDataMutationData): IValidSetTextDataMutationData | undefined {
    if (pageId && shapeId) {
        return {
            type: MutationType.SetTextData,
            pageId,
            shapeId,
            text: text ?? '',
            row: row ?? 0,
            col: col ?? 0,
            textProperties: textProperties ?? [],
        };
    }
}

export function ValidSetTextBodyPropertiesMutationData({
    pageId,
    shapeId,
    row,
    col,
    textBodyProperties,
    textListStyle,
}: ISetTextBodyPropertiesMutationData): IValidSetTextBodyPropertiesMutationData | undefined {
    if (pageId && shapeId) {
        return {
            type: MutationType.SetTextBodyProperties,
            pageId,
            shapeId,
            row: row ?? 0,
            col: col ?? 0,
            textBodyProperties,
            textListStyle,
        };
    }
}

// eslint-disable-next-line complexity
export function ValidUpdateResourceMapMutationData({
    id = '',
    resourceType,
    imageDescriptor,
    videoDescriptor,
    audioDescriptor,
    fontDescriptor,
}: IUpdateResourceMapMutationData): IValidUpdateResourceMapMutationData | undefined {
    if (resourceType === ResourceType.Image && imageDescriptor) {
        return {
            type: MutationType.UpdateResourceMap,
            id,
            resourceType,
            imageDescriptor,
        };
    }

    if (resourceType === ResourceType.Video && videoDescriptor) {
        return {
            type: MutationType.UpdateResourceMap,
            id,
            resourceType,
            videoDescriptor,
        };
    }

    if (resourceType === ResourceType.Audio && audioDescriptor) {
        return {
            type: MutationType.UpdateResourceMap,
            id,
            resourceType,
            audioDescriptor,
        };
    }

    if (resourceType === ResourceType.Font && fontDescriptor) {
        return {
            type: MutationType.UpdateResourceMap,
            id,
            resourceType,
            fontDescriptor,
        };
    }

    return;
}

function ValidTableRangeData(range: ITableRangeData | undefined): Required<ITableRangeData> | undefined {
    if (!range) return;

    const { startRowIndex = 0, startColIndex = 0, rowSpan = 0, colSpan = 0 } = range;
    if (startRowIndex < 0 || startColIndex < 0 || rowSpan <= 0 || colSpan <= 0) {
        return;
    }

    return {
        startRowIndex,
        startColIndex,
        rowSpan,
        colSpan,
    };
}

export function ValidSetNotesSizeMutationData({
    size,
}: ISetNotesSizeMutationData): IValidSetNotesSizeMutationData | undefined {
    if (size) {
        return {
            type: MutationType.SetNotesSize,
            size,
        };
    }
}

export function ValidComputeGroupTransformMutationData({
    pageId,
    groupId,
    parentId,
    shapeIds,
}: IComputeGroupTransformMutationData): IValidComputeGroupTransformMutationData | undefined {
    if (pageId && groupId) {
        return {
            type: MutationType.ComputeGroupTransform,
            pageId,
            groupId,
            shapeIds: shapeIds ?? [],
            parentId: parentId ?? '',
        };
    }
}

export function ValidSetDiagramDataMutationData({
    pageId,
    shapeId,
    dataModel,
}: ISetDiagramDataMutationData): IValidSetDiagramDataMutationData | undefined {
    if (pageId && shapeId && dataModel) {
        return {
            type: MutationType.SetDiagramData,
            pageId,
            shapeId,
            dataModel,
        };
    }
}

export function ValidSetDiagramDrawingMutationData({
    pageId,
    shapeId,
    drawing,
}: ISetDiagramDrawingMutationData): IValidSetDiagramDrawingMutationData | undefined {
    if (pageId && shapeId && drawing) {
        return {
            type: MutationType.SetDiagramDrawing,
            pageId,
            shapeId,
            drawing,
        };
    }
}

export function ValidSetDiagramsMutationData({
    pageId,
    shapeId,
    layoutDef,
    styleDef,
    colorDef,
}: ISetDiagramsMutationData): IValidSetDiagramsMutationData | undefined {
    if (pageId && shapeId && layoutDef && styleDef && colorDef) {
        return {
            type: MutationType.SetDiagrams,
            pageId,
            shapeId,
            layoutDef,
            styleDef,
            colorDef,
        };
    }
}

export function ValidAddCommentMutationData({
    pageId,
    commentType,
    groupId,
    shapeId,
    anchorPosition,
    textAnchor,
    createdTimeStamp,
}: IAddCommentMutationData): IValidAddCommentMutationData | undefined {
    if (!pageId || !groupId) return;
    if (commentType === CommentType.element && !shapeId) return;
    if (commentType === CommentType.text && !(shapeId && textAnchor)) return;
    return {
        type: MutationType.AddComment,
        pageId,
        commentType: commentType ?? CommentType.PBDEFAULT,
        groupId,
        shapeId: shapeId ?? '',
        anchorPosition,
        textAnchor,
        createdTimeStamp,
    };
}

export function ValidRemoveCommentMutationData({
    pageId,
    commentType,
    groupId,
    shapeId,
}: IRemoveCommentMutationData): IValidRemoveCommentMutationData | undefined {
    if (!pageId || !groupId) return;
    if ((commentType === CommentType.element || commentType === CommentType.text) && !shapeId) return;
    return {
        type: MutationType.RemoveComment,
        pageId,
        commentType: commentType ?? CommentType.PBDEFAULT,
        groupId,
        shapeId: shapeId ?? '',
    };
}

export function ValidSetCommentPropertiesMutationData({
    pageId,
    commentType,
    groupId,
    shapeId,
    anchorPosition,
    textAnchor,
}: ISetCommentPropertiesMutationData): IValidSetCommentPropertiesMutationData | undefined {
    if (!pageId || !groupId) return;
    if (commentType === CommentType.element && !shapeId) return;
    if (commentType === CommentType.text && !(shapeId && textAnchor)) return;
    return {
        type: MutationType.SetCommentProperties,
        pageId,
        commentType: commentType ?? CommentType.PBDEFAULT,
        groupId,
        shapeId: shapeId ?? '',
        anchorPosition,
        textAnchor,
    };
}

export function ValidSetDataExtensionData({
    targetType,
    pageId,
    targetId,
    key,
    value,
}: ISetDataExtensionMutationData): IValidSetDataExtensionMutationData | undefined {
    if (key && value && pageId !== '' && targetId !== '') {
        return {
            type: MutationType.SetDataExtension,
            targetType,
            pageId,
            targetId,
            key,
            value,
        };
    }
}

export function ValidRemoveDataExtensionData({
    targetType,
    pageId,
    targetId,
    key,
}: IRemoveDataExtensionMutationData): IValidRemoveDataExtensionMutationData | undefined {
    if (pageId !== '' && targetId !== '') {
        return {
            type: MutationType.RemoveDataExtension,
            targetType,
            pageId,
            targetId,
            key,
        };
    }
}
